import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowUpCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useToast } from "@/hooks/use-toast";

const MODULE_INFO: Record<string, { plan: string; planKey: string; features: string[] }> = {
  coverage_analytics: {
    plan: "Professional",
    planKey: "professional",
    features: [
      "Journalist coverage scoring (0–100)",
      "Per-article AI quality assessment",
      "Coverage leaderboard and beat breakdown",
      "Nightly aggregate score recomputation",
    ],
  },
  awards: {
    plan: "Professional",
    planKey: "professional",
    features: [
      "Award category management",
      "Nomination cycles with deadlines",
      "Winner declaration and permanent record",
      "Journalist incentive programme",
    ],
  },
};

interface Props {
  module: string;
  open: boolean;
  onClose: () => void;
  onNavigate?: (view: string) => void;
}

export function UpgradeDialog({ module, open, onClose, onNavigate }: Props) {
  const info = MODULE_INFO[module];
  const planName = info?.plan ?? "Professional";
  const planKey = info?.planKey ?? "professional";
  const displayName = module.replace(/_/g, " ");
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [provider, setProvider] = useState<"stripe" | "paystack">("paystack");

  const handleUpgrade = async () => {
    if (!orgId) {
      toast({ title: "Organisation not loaded. Please refresh.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { org_id: orgId, plan: planKey, billing_cycle: billingCycle, provider },
      });
      if (error) throw new Error(error.message ?? "Checkout failed");
      if (!data?.checkout_url) throw new Error("No checkout URL returned");
      window.location.href = data.checkout_url;
    } catch (err: any) {
      toast({ title: err.message ?? "Upgrade failed", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-[#C8A84B]/15 flex items-center justify-center flex-shrink-0">
              <ArrowUpCircle size={18} className="text-[#C8A84B]" />
            </div>
            <DialogTitle className="text-[#0D2240] text-[15px]">
              {planName} Plan Required
            </DialogTitle>
          </div>
        </DialogHeader>

        <p className="text-[12.5px] text-muted-foreground text-justify leading-relaxed">
          The{" "}
          <strong className="text-foreground capitalize">{displayName}</strong>{" "}
          module is included in the{" "}
          <strong className="text-foreground">{planName}</strong> plan.
        </p>

        {info?.features.length && (
          <div className="mt-3 bg-muted rounded-lg p-3">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
              Included in {planName}
            </div>
            <ul className="space-y-1.5">
              {info.features.map(f => (
                <li key={f} className="flex items-start gap-2 text-[12px]">
                  <span className="text-[#006B3C] mt-0.5 flex-shrink-0">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Billing options */}
        <div className="mt-4 space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`flex-1 py-1.5 rounded-lg border text-[12px] font-medium transition-colors ${
                billingCycle === "monthly"
                  ? "border-[#C8A84B] bg-[#C8A84B]/10 text-[#C8A84B]"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`flex-1 py-1.5 rounded-lg border text-[12px] font-medium transition-colors ${
                billingCycle === "annual"
                  ? "border-[#C8A84B] bg-[#C8A84B]/10 text-[#C8A84B]"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              Annual <span className="text-[10px] text-green-600 ml-1">Save 17%</span>
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setProvider("paystack")}
              className={`flex-1 py-1.5 rounded-lg border text-[12px] font-medium transition-colors ${
                provider === "paystack"
                  ? "border-[#006B3C] bg-[#006B3C]/10 text-[#006B3C]"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              Paystack (₦)
            </button>
            <button
              onClick={() => setProvider("stripe")}
              className={`flex-1 py-1.5 rounded-lg border text-[12px] font-medium transition-colors ${
                provider === "stripe"
                  ? "border-[#635BFF] bg-[#635BFF]/10 text-[#635BFF]"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              Stripe ($)
            </button>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-border rounded-lg text-[13px] text-muted-foreground hover:bg-muted"
          >
            Close
          </button>
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="flex-1 bg-[#006B3C] hover:bg-[#005A32] text-white text-[13px] h-9"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin mr-1" /> Processing…</>
            ) : (
              "Upgrade Now"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
